import { useIntl, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type Actions } from '@atlaskit/onboarding/types';

export const useSpotlightActions = ({
	progress,
	onNext,
	onBack,
	source,
}: {
	progress: { isLast: boolean; isFirst: boolean; step: number; steps: number };
	onNext: () => void;
	onBack: () => void;
	source: string;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isLast, isFirst, step, steps } = progress;
	const actions: Actions = [
		{
			onClick: () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'next',
						source,
					},
				}).fire();
				onNext();
			},

			text: formatMessage(isLast ? messages.done : messages.next),
		},
		...(isFirst
			? []
			: ([
					{
						onClick: () => {
							createAnalyticsEvent({
								type: 'sendUIEvent',
								data: {
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'back',
									source,
								},
							}).fire();
							onBack();
						},
						text: formatMessage(messages.back),
						appearance: 'subtle',
					},
				] as Actions)),
	];

	const actionsBeforeElement =
		steps === 1
			? null
			: formatMessage(messages.progress, {
					step,
					steps,
				});

	return { actions, actionsBeforeElement };
};

const messages = defineMessages({
	back: {
		id: 'modernize-changeboarding.useSpotlightActions.back',
		defaultMessage: 'Back',
		description: 'Label for a button to show the previous onboarding spotlight',
	},
	next: {
		id: 'modernize-changeboarding.useSpotlightActions.next',
		defaultMessage: 'Next',
		description: 'Label for button to show the next onboarding spotlight',
	},
	done: {
		id: 'modernize-changeboarding.useSpotlightActions.done',
		defaultMessage: 'Done',
		description: 'Label for button to finish onboarding',
	},
	progress: {
		id: 'modernize-changeboarding.useSpotlightActions.progress',
		defaultMessage: '{step}/{steps}',
		description: 'A label that tracks which step of the tour the user is currently on',
	},
});
